import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Close as CloseIcon, DriveFileMove as MoveIcon } from '@mui/icons-material';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

/** Channel item returned from channels list API (subset used for move target) */
export interface ChannelForMove {
  id: string;
  name: string;
  channel_type?: 'email' | 'message' | 'voice';
  messaging_provider?: 'regular' | 'whatsapp' | 'slack' | 'google_chat' | 'google_chat_inbox' | 'teams';
  /** API may return provider at top level (e.g. "regular", "whatsapp") */
  provider?: string;
  channel_settings?: { provider?: string };
  is_archived?: boolean;
}

export interface ApiThreadForMove {
  id: string;
  title: string;
  description?: string;
}

/** Raw channel item from message-channels API */
interface MessageChannelItem {
  id: string;
  name: string;
  description?: string;
  workspace_id?: string;
  workspace_name?: string;
  is_archived?: boolean;
  channel_type?: number | string;
  provider?: string;
  [key: string]: unknown;
}

interface MoveThreadModalProps {
  open: boolean;
  onClose: () => void;
  /** Thread to move */
  thread: ApiThreadForMove | null;
  /** Current channel ID (excluded from list) */
  currentChannelId: string | undefined;
  /** Workspace ID used to fetch message channels (required for channel list) */
  workspaceId: string | undefined;
  onMoveSuccess: (targetChannelId: string) => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
}

/**
 * Modal to move a thread to another channel.
 * Shows only existing regular message channels (channel_type=message, messaging_provider=regular).
 */
const MoveThreadModal: React.FC<MoveThreadModalProps> = ({
  open,
  onClose,
  thread,
  currentChannelId,
  workspaceId,
  onMoveSuccess,
  showError,
  showSuccess,
}) => {
  const [channels, setChannels] = useState<ChannelForMove[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [moving, setMoving] = useState<boolean>(false);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);

  // Fetch message channels for the current workspace when modal opens (API: POST /channels/message-channels)
  const fetchRegularChannels = useCallback(async () => {
    if (!open) return;
    if (!workspaceId) {
      setChannels([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const params = new URLSearchParams({
        workspace_id: workspaceId,
        is_archived: 'false',
      });
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.MESSAGE_CHANNELS}?${params}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to load channels: ${response.status}`);
      }
      const data: MessageChannelItem[] = await response.json();
      const list = Array.isArray(data) ? data : [];
      // Map to ChannelForMove and exclude current channel and archived
      const mapped: ChannelForMove[] = list
        .filter((c) => c.id !== currentChannelId && !c.is_archived)
        .map((c) => ({
          id: c.id,
          name: c.name,
          description: c.description,
          channel_type: typeof c.channel_type === 'string' ? c.channel_type as 'email' | 'message' | 'voice' : 'message',
          provider: typeof c.provider === 'string' ? c.provider : undefined,
          is_archived: c.is_archived,
        }));
      setChannels(mapped);
      setSelectedChannelId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load channels';
      showError(message);
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, [open, workspaceId, currentChannelId, showError]);

  useEffect(() => {
    if (open && thread) {
      fetchRegularChannels();
    }
  }, [open, thread, fetchRegularChannels]);

  const handleMove = async () => {
    if (!thread || !selectedChannelId) return;
    setMoving(true);
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.MESSAGES.MOVE_THREAD}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            thread_id: thread.id,
            new_channel_id: selectedChannelId,
          }),
        }
      );
      if (!response.ok) {
        const errBody = await response.text();
        let msg = `Move failed (${response.status})`;
        try {
          const parsed = JSON.parse(errBody);
          // Prefer API error fields: detail (e.g. FastAPI), then message, then error
          const apiMsg = parsed.detail ?? parsed.message ?? parsed.error;
          if (apiMsg && typeof apiMsg === 'string') msg = apiMsg;
          else if (Array.isArray(parsed.detail)) msg = parsed.detail.map((d: unknown) => typeof d === 'string' ? d : JSON.stringify(d)).join(' ');
        } catch {
          if (errBody) msg = errBody.slice(0, 200);
        }
        throw new Error(msg);
      }
      showSuccess('Thread moved successfully.');
      onMoveSuccess(selectedChannelId);
      onClose();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to move thread.');
    } finally {
      setMoving(false);
    }
  };

  const handleClose = () => {
    if (!moving) {
      setSelectedChannelId(null);
      onClose();
    }
  };

  const selectedChannel = channels.find((c) => c.id === selectedChannelId) ?? null;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={moving}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoveIcon color="primary" />
          Move Thread
        </Box>
        <IconButton aria-label="Close" onClick={handleClose} disabled={moving} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {thread && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move &quot;{thread.title}&quot; to another regular channel.
          </Typography>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : channels.length === 0 ? (
          <Typography color="text.secondary">No other regular channels available.</Typography>
        ) : (
          <Autocomplete
            id="move-thread-channel-select"
            value={selectedChannel}
            onChange={(_, newValue) => setSelectedChannelId(newValue?.id ?? null)}
            options={channels}
            getOptionLabel={(option) => option.name}
            filterOptions={(options, { inputValue }) =>
              options.filter((ch) =>
                ch.name.toLowerCase().includes((inputValue || '').trim().toLowerCase())
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select channel"
                placeholder="Search or select..."
                variant="outlined"
                size="medium"
              />
            )}
            sx={{ mt: 0, minWidth: 200 }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={handleClose} disabled={moving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleMove}
          disabled={!selectedChannelId || moving}
          startIcon={moving ? <CircularProgress size={16} color="inherit" /> : <MoveIcon />}
        >
          {moving ? 'Moving...' : 'Move here'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveThreadModal;
